import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdVerifiedUser } from 'react-icons/md';
import {
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
  StatusBadge,
} from '../../../../components/ui';
import {
  LOCAL_PROFESSIONAL_OPTIONS,
  MAJOR_CATEGORY_OPTIONS,
  VENDOR_VERIFICATION_OPTIONS,
} from '../../../../data/master/vendors';
import { toApiStatus } from '../../../../models/_helpers';
import {
  blockVendor,
  updateVendor,
  verifyVendor,
} from '../../../../repositories/vendor.repository';
import { vendorPortalUrl } from '../../../../utils/vendorPortalToken';

function badgeStatus(status) {
  if (status === 'Verified') return 'verified';
  if (status === 'Rejected' || status === 'Blocked') return 'error';
  return 'pending';
}

export function VendorVerificationStatusModal({ isOpen, onClose, vendor, onSaved }) {
  const toast = useToast();
  const [status, setStatus] = useState('Pending');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [localProfessional, setLocalProfessional] = useState('');
  const [majorCategory, setMajorCategory] = useState('');
  const [classificationError, setClassificationError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !vendor) return;
    setStatus(vendor.verificationStatus || 'Pending');
    setReason(
      vendor.verificationStatus === 'Blocked'
        ? vendor.blockReason || ''
        : vendor.rejectionReason || '',
    );
    setReasonError('');
    setLocalProfessional(vendor.localProfessional || '');
    setMajorCategory(vendor.majorCategory || '');
    setClassificationError('');
  }, [isOpen, vendor]);

  if (!vendor) return null;

  const needsReason = status === 'Rejected' || status === 'Blocked';
  const needsClassification = status === 'Verified';

  const handleSave = async (e) => {
    e.preventDefault();
    if (needsClassification && !localProfessional) {
      setClassificationError('Select LOCAL or PROFESSIONAL before verifying');
      return;
    }
    if (needsReason) {
      const trimmed = reason.trim();
      if (trimmed.length < 5) {
        setReasonError(
          status === 'Blocked'
            ? 'Enter a clear block reason (min 5 characters)'
            : 'Enter a clear rejection reason (min 5 characters)',
        );
        return;
      }
      if (trimmed.length > 500) {
        setReasonError('Max 500 characters');
        return;
      }
    }

    if (vendor.id == null) {
      toast({ title: 'Missing vendor id', status: 'error', position: 'top-right' });
      return;
    }

    const wasVerified = vendor.verificationStatus === 'Verified';
    const becomingVerified = status === 'Verified';

    setSaving(true);
    try {
      let updated = null;
      if (status === 'Blocked') {
        updated = await blockVendor(vendor.id, { blockReason: reason.trim() });
      } else if (status === 'Verified' || status === 'Rejected') {
        updated = await verifyVendor(vendor.id, {
          verificationStatus: toApiStatus(status),
          localProfessional: needsClassification
            ? localProfessional
            : vendor.localProfessional || undefined,
          majorCategory: needsClassification
            ? majorCategory || undefined
            : vendor.majorCategory || undefined,
          rejectionReason: status === 'Rejected' ? reason.trim() : null,
        });
      } else {
        updated = await updateVendor(vendor.id, {
          verificationStatus: 'Pending',
          rejectionReason: '',
          blockReason: '',
          active: true,
        });
      }

      const portalToken = updated?.portalToken || '';
      toast({
        title: 'Vendor status updated',
        description:
          becomingVerified && !wasVerified && portalToken
            ? `${vendor.tradeAgencyName} · Verified · portal link generated`
            : `${vendor.tradeAgencyName} · ${status}`,
        status: 'success',
        position: 'top-right',
        duration: becomingVerified && portalToken ? 6000 : 4000,
      });
      if (becomingVerified && portalToken) {
        toast({
          title: 'Vendor request link',
          description: vendorPortalUrl(portalToken),
          status: 'info',
          position: 'top-right',
          duration: 8000,
          isClosable: true,
        });
      }
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update vendor status',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        overflow="hidden"
      >
        <ModalHeader pb={2}>Update verification status</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack align="stretch" spacing={4}>
            <Box
              p={4}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="44px"
                  h="44px"
                  align="center"
                  justify="center"
                  borderRadius="sm"
                  bg="bg.amberTint"
                  flexShrink={0}
                >
                  <MdVerifiedUser size={22} color="#1A1A1A" />
                </Flex>
                <Box minW={0}>
                  <Text fontWeight="700" fontSize="15px" noOfLines={1} color="text.primary">
                    {vendor.tradeAgencyName}
                  </Text>
                  <Flex align="center" gap={2} mt={1} wrap="wrap">
                    <Text fontSize="13px" fontFamily="mono" color="text.secondary">
                      {vendor.vendorId}
                    </Text>
                    <StatusBadge
                      status={badgeStatus(vendor.verificationStatus || 'Pending')}
                    >
                      Current: {vendor.verificationStatus || 'Pending'}
                    </StatusBadge>
                  </Flex>
                </Box>
              </Flex>
            </Box>

            <AppSelect
              label="New status"
              isRequired
              value={status}
              onChange={(v) => {
                setStatus(v);
                setReasonError('');
                setClassificationError('');
                if (v !== 'Rejected' && v !== 'Blocked') setReason('');
              }}
              options={VENDOR_VERIFICATION_OPTIONS}
            />

            {needsClassification ? (
              <Box
                p={4}
                bg="bg.input"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="border.subtle"
              >
                <Heading size="xs" mb={1}>
                  Vendor classification
                </Heading>
                <Text fontSize="13px" color="text.secondary" mb={4} lineHeight="1.45">
                  Recorded by staff at verification — not collected during self
                  registration.
                </Text>
                <VStack align="stretch" spacing={4}>
                  <AppSelect
                    label="LOCAL / PROFESSIONAL"
                    isRequired
                    value={localProfessional}
                    onChange={(v) => {
                      setLocalProfessional(v || '');
                      setClassificationError('');
                    }}
                    options={[
                      { value: '', label: 'Select…' },
                      ...LOCAL_PROFESSIONAL_OPTIONS,
                    ]}
                    isInvalid={!!classificationError}
                    errorMessage={classificationError}
                  />
                  <AppSelect
                    label="Major Category"
                    value={majorCategory}
                    onChange={(v) => setMajorCategory(v || '')}
                    options={[
                      { value: '', label: 'Select…' },
                      ...MAJOR_CATEGORY_OPTIONS,
                    ]}
                  />
                </VStack>
              </Box>
            ) : null}

            {needsReason ? (
              <Box
                p={4}
                bg="bg.errorTint"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="status.error"
              >
                <Heading size="xs" mb={1} color="status.error">
                  {status === 'Blocked' ? 'Block reason required' : 'Rejection reason required'}
                </Heading>
                <Text fontSize="13px" color="text.secondary" mb={4} lineHeight="1.45">
                  {status === 'Blocked'
                    ? 'Explain why this vendor is blocked. Their request link will stop working.'
                    : 'Explain why this vendor is rejected. Shown on their profile.'}
                </Text>
                <AppTextarea
                  label="Reason"
                  isRequired
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError('');
                  }}
                  placeholder={
                    status === 'Blocked'
                      ? 'e.g. Repeated policy violation / safety incident'
                      : 'e.g. GST certificate missing / Aadhaar KYC incomplete'
                  }
                  rows={4}
                  maxLength={500}
                  isInvalid={!!reasonError}
                  errorMessage={reasonError}
                  helperText={`${reason.trim().length}/500`}
                />
              </Box>
            ) : (
              <Box
                p={3}
                bg="bg.amberTint"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="utkarsh.amber"
              >
                <Text fontSize="13px" color="text.primary" lineHeight="1.45">
                  {status === 'Verified'
                    ? wasVerifiedHint(vendor)
                    : 'Pending keeps the vendor awaiting review.'}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Update status
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function wasVerifiedHint(vendor) {
  if (vendor.verificationStatus === 'Verified' && vendor.portalToken) {
    return 'Already verified — existing request link is kept.';
  }
  return 'Marking as Verified generates a unique request link for this vendor.';
}
